import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { 
  SmsMessage, 
  SmsSendRequest, 
  BatchSmsRequest, 
  PaginationParams, 
  PaginationResponse 
} from '@/types'
import apiClient from '@/lib/api/client'
import { sendSms as sdkSendSms, batchSendSms as sdkBatchSendSms, tenantGetSmsRecords as sdkGetRecords, tenantGetSmsStatus as sdkGetSmsStatus, tenantSendSms } from '@/lib/api/generated/sdk.gen'

// 发送单条短信
export const useSendSms = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: SmsSendRequest): Promise<SmsMessage[]> => {
      // 调试：打印发送的数据
      console.log('=== SMS Send Debug ===')
      console.log('Sending data:', {
        mobile: data.phones[0],
        content: data.content,
        templateCode: data.templateId,
        templateParams: data.variables
      })
      console.log('=== End Debug ===')
      
      // 使用租户短信发送API
      const res: any = await tenantSendSms({ 
        client: apiClient, 
        body: {
          mobile: data.phones[0], // 暂时只支持单条发送
          content: data.content || '',
          templateCode: data.templateId,
          templateParams: data.variables && Object.keys(data.variables).length > 0 ? data.variables : null
        } as any, 
        responseStyle: 'fields', 
        throwOnError: false 
      })
      const payload: any = (res as any)?.data ?? res
      return (payload?.records || payload?.data || payload || []) as SmsMessage[]
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sms', 'records'] })
      queryClient.invalidateQueries({ queryKey: ['overview', 'stats'] })
    },
  })
}

// 批量发送短信
export const useBatchSendSms = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: BatchSmsRequest): Promise<{ taskId: string; total: number }> => {
      const res: any = await sdkBatchSendSms({ client: apiClient, body: data as any, responseStyle: 'fields', throwOnError: false })
      const payload: any = (res as any)?.data ?? res
      return payload as { taskId: string; total: number }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sms', 'records'] })
    },
  })
}

// 获取发送记录
export const useSmsRecords = (params: PaginationParams & { 
  status?: string
  startDate?: string
  endDate?: string
  phone?: string
}) => {
  return useQuery({
    queryKey: ['sms', 'records', params],
    queryFn: async (): Promise<PaginationResponse<SmsMessage>> => {
      const res: any = await sdkGetRecords({ client: apiClient, responseStyle: 'fields', throwOnError: false, query: params as any })
      // API现在直接返回分页数据，不再嵌套在data字段中
      const payload: any = (res as any)?.data ?? res
      return (payload as any) as PaginationResponse<SmsMessage>
    },
  })
}

// 获取发送详情
export const useSmsDetail = (id: string) => {
  return useQuery({
    queryKey: ['sms', 'detail', id],
    queryFn: async (): Promise<SmsMessage> => {
      const res: any = await sdkGetSmsStatus({ client: apiClient, responseStyle: 'fields', throwOnError: false, path: { messageId: id } as any })
      const payload: any = (res as any)?.data ?? res
      return (payload as any) as SmsMessage
    },
    enabled: !!id,
  })
}

// 重发短信
export const useResendSms = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<SmsMessage> => {
      // 暂无明确重发接口，先直接返回 Promise.reject 以提示未实现
      return Promise.reject(new Error('Not implemented'))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sms', 'records'] })
    },
  })
}

// 获取发送统计
export const useSmsStats = (params: { 
  startDate?: string
  endDate?: string
  groupBy?: 'day' | 'hour'
}) => {
  return useQuery({
    queryKey: ['sms', 'stats', params],
    queryFn: async () => {
      // 暂用 records 接口侧的统计，后续切换至 sdk.stats 如有
      const res: any = await sdkGetRecords({ client: apiClient, responseStyle: 'fields', throwOnError: false, query: params as any })
      return (res as any)?.data ?? res
    },
  })
}